import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Row, Col, Spinner,
} from 'react-bootstrap';
import envConfig from '../../../envConfig';
import decodeBase64Param from '../../../utility/decodeBase64Param';
import FreeTrialMarketingPanel from './FreeTrialMarketingPanel';
import FreeTrialLoginForm from './FreeTrialLoginForm';

const AUTO_LOGIN_ENDPOINT = '/app/gateway/login-one-time-token';

const STATUS = {
  PENDING: 'pending',
  FAILED: 'failed',
};

const extractServerErrors = (responseData) => {
  if (!responseData) return [];
  if (responseData.success === false) {
    const list = responseData.data || responseData.content || responseData.errors || [];
    return Array.isArray(list) ? list : [];
  }
  const list = responseData.content || responseData.errors || [];
  return Array.isArray(list) ? list : [];
};

const OneTimeLoginPage = () => {
  const { sid, em, hash } = useParams();

  const subscriptionId = useMemo(() => decodeBase64Param(sid), [sid]);
  const email = useMemo(() => decodeBase64Param(em), [em]);

  const [status, setStatus] = useState(STATUS.PENDING);
  const [autoLoginErrors, setAutoLoginErrors] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const attemptAutoLogin = async () => {
      try {
        const formData = new URLSearchParams();
        formData.append('subscriptionId', Number(subscriptionId));
        formData.append('emailAddress', email);
        formData.append('tokenHash', hash);

        const response = await axios.post(
          `${envConfig.raaUrl}${AUTO_LOGIN_ENDPOINT}`,
          formData,
          {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            validateStatus: (s) => s >= 200 && s < 400,
          },
        );

        if (cancelled) return;

        if (response.data && response.data.success) {
          window.location.replace('/s/welcome');
          return;
        }

        setAutoLoginErrors(extractServerErrors(response.data));
        setStatus(STATUS.FAILED);
      } catch (err) {
        if (cancelled) return;
        const serverErrors = extractServerErrors(err.response && err.response.data);
        setAutoLoginErrors(serverErrors.length > 0 ? serverErrors : [{ field: 'generic', message: 'Unexpected error.' }]);
        setStatus(STATUS.FAILED);
      }
    };

    if (!subscriptionId || !email || !hash) {
      window.location.replace('/gateway');
      return undefined;
    }

    attemptAutoLogin().then();
    return () => { cancelled = true; };
  }, [subscriptionId, email, hash]);

  if (status === STATUS.PENDING) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" />
        <div>Signing you in…</div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="align-items-start">
        <Col xs={12} md={6} xl={6} className="free-trial-divider pb-4 pb-md-0 mb-4 mb-md-0 pr-md-4">
          <FreeTrialMarketingPanel />
        </Col>
        <Col xs={12} md={6} xl={4} className="pl-md-4 mt-4 mx-xl-auto">
          <FreeTrialLoginForm
            subscriptionId={subscriptionId}
            initialEmail={email || ''}
            autoLoginErrors={autoLoginErrors}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default OneTimeLoginPage;
